import { EVENT_SITES, itly } from '@crossbeam/itly';

import useMinimalApp from '@/composables/useMinimalApp';
import appConfig, { useStagingApi } from '@/config';
import { TEMP_NEW_ONBOARDING_SMART } from '@/constants/feature_flags';
import { isVerified } from '@/constants/verification_status';
import { getAndClearLSNextUrl, ls } from '@/local_storage';
import { useBillingStore } from '@/stores';
import { useFeatureFlagStore } from '@/stores/FeatureFlagStore';
import { disableIntercom } from '@/trackers/intercom';
import urls from '@/urls';
import { SSO_PARAM } from '@/utils';
import { isOrgInSmartOnboardingTestGroup } from '@/utils/abTestGate';

export async function routingOverride({ from, to, store }) {
  const billingStore = useBillingStore();

  if (!store.isLoggedIn) {
    return { name: 'login' };
  }

  const { isMinimalView } = useMinimalApp();

  const SALES_ROUTES = [
    'deal-navigator',
    'generate-pipeline',
    'lists',
    'login',
    'logout',
    'co-sell',
    'collaborate',
    'profile/preferences',
    'reports',
  ];

  await billingStore.readySync;

  const isAcademyLogin = from.name === 'academy';

  // If user has no core access and they aren't trying to log into the
  // crossbeam academy application (crossbeam.academy.com), then route to sales app
  const routeToSalesApp = store.hasSalesEdgeOnly && !isAcademyLogin;

  if (isMinimalView && !store.isAuthFromLocalStorage) {
    const ssoId = sessionStorage.getItem(SSO_PARAM);
    if (ssoId) {
      return { name: 'login', query: { sso: ssoId } };
    }
    return { name: 'coming_soon' };
  } else if (store.userCanTryToLinkAccounts && from.name !== 'link-accounts') {
    return { name: 'link-accounts' };
  } else if (routeToSalesApp) {
    window.location.replace(window.origin.replace('app', 'sales'));
  } else if (store.userNeedsToRegister) {
    return { name: 'company_setup' };
  } else if (store.userHasNoOrg) {
    return { name: 'no_org' };
  } else if (store.currentOrg?.is_dormant) {
    return { name: 'dormant_company' };
  } else if (!isVerified(store.currentOrg)) {
    return { name: 'verify_company' };
  } else if (
    store.hasSalesOnlyRole &&
    !SALES_ROUTES.some((salesRoute) => to.path.includes(salesRoute))
  ) {
    return {
      name: 'generate-pipeline',
    };
  }
  return null;
}

export const isAuthorizedExternalRedirect = (nextUrl) => {
  if (typeof nextUrl !== 'string' || !nextUrl) return false;
  try {
    const target = new URL(nextUrl);
    return (
      target.protocol === 'https:' &&
      target.hostname.endsWith('.chromiumapp.org')
    );
  } catch {
    return false;
  }
};

/* Exact-origin allowlist check for the Sales Edge ("Partnered") redirect. The
 * allowed origin is derived from the per-environment appConfig.salesEdgeRedirectUrl
 * (prod https://sales.crossbeam.com, dev https://sales.crossbeam-dev.com,
 * staging/test https://sales.cb.c7m.io) so no new config surface is needed.
 * Comparing new URL(...).origin closes the startsWith() prefix bypass where a
 * host like sales.crossbeam.com.evil.example would satisfy a naive prefix match. */
export const isAllowedSalesEdgeRedirect = (nextUrl) => {
  if (
    typeof nextUrl !== 'string' ||
    !nextUrl ||
    !appConfig.salesEdgeRedirectUrl
  )
    return false;
  try {
    return (
      new URL(nextUrl).origin === new URL(appConfig.salesEdgeRedirectUrl).origin
    );
  } catch {
    return false;
  }
};

/* Write-time sanitizer for ?next= values before they are persisted to
 * localStorage. Permits same-origin URLs (including relative internal paths
 * resolved against the app origin), the exact Sales Edge origin, and
 * *.chromiumapp.org callbacks; returns undefined for anything else so a hostile
 * value never reaches storage. */
export const sanitizeNextUrl = (nextUrl) => {
  if (typeof nextUrl !== 'string' || !nextUrl) return undefined;
  try {
    const target = new URL(nextUrl, window.location.origin);
    if (target.origin === window.location.origin) return nextUrl;
  } catch {
    return undefined;
  }
  if (
    isAllowedSalesEdgeRedirect(nextUrl) ||
    isAuthorizedExternalRedirect(nextUrl)
  )
    return nextUrl;
  return undefined;
};

export function beforeEnterMain(to, _from, next, store) {
  const newSession = ls.newSession.get();

  if (newSession && !store.userHasNoOrg) {
    itly.userLoggedIn({ event_site: EVENT_SITES.LOGIN_CROSSBEAM });
    ls.newSession.set();
  }
  const publicInvite = ls.publicInvite.get();
  const proposalAcceptInfo = ls.proposalAcceptInfo.get();

  const isOnboardingCompleted = store?.currentOrg?.setup_menu_hidden;

  let nextUrlOrRoute;
  if (publicInvite) {
    if (isOnboardingCompleted) {
      nextUrlOrRoute = { name: 'accept_public_invite' };
    } else {
      const featureFlagStore = useFeatureFlagStore();
      const orgId = store?.currentOrg?.id;
      const hasCompletedExtraStep =
        ls.acceptedInviteSender.get()?.orgId === orgId ||
        ls.publicInviteSentOrg.get()?.orgId === orgId;
      const isSmartOnboarding =
        featureFlagStore.hasFeatureFlag(TEMP_NEW_ONBOARDING_SMART) &&
        isOrgInSmartOnboardingTestGroup(orgId);
      nextUrlOrRoute =
        isSmartOnboarding && !hasCompletedExtraStep
          ? { name: 'onboarding' }
          : { name: 'onboarding_accept_public_invite' };
    }
  } else if (proposalAcceptInfo) {
    if (isOnboardingCompleted) {
      const query = {
        proposal_id: proposalAcceptInfo.proposal.id,
        proposal_acceptance_code: proposalAcceptInfo.acceptance_code,
      };
      nextUrlOrRoute = {
        name: 'accept_proposal',
        query,
      };
    } else {
      nextUrlOrRoute = { name: 'onboarding' };
    }
  } else {
    const fallback = { name: 'home' };
    if (to?.query) {
      const { state: _state, ...query } = to.query;
      fallback.query = query;
    }
    nextUrlOrRoute = getAndClearLSNextUrl() || fallback;
  }

  /* This is a redirect from Partnered, send them back there after logging in,
   * instead of sending them to the crossbeam application */
  const isPartneredLogin = isAllowedSalesEdgeRedirect(nextUrlOrRoute);

  if (isPartneredLogin) {
    window.open(nextUrlOrRoute, '_self');
    return;
  }

  if (isAuthorizedExternalRedirect(nextUrlOrRoute)) {
    window.location.replace(nextUrlOrRoute);
    return;
  }
  next(nextUrlOrRoute);
}

export const handleLogout = (nextUrl) => {
  for (const storageItem of Object.values(ls)) {
    if (storageItem.clearOnLogout) storageItem.set();
  }
  const devOrigin = 'https://app.crossbeam-dev.com';
  const baseOrigin = useStagingApi() ? devOrigin : window.location.origin;

  const returnToUrl = new URL(nextUrl, baseOrigin);

  if (!import.meta.env.DEV && window.Appcues) window.Appcues.reset();
  disableIntercom();

  const params = new URLSearchParams(window.location.search);
  params.forEach((value, key) => {
    returnToUrl.searchParams.set(key, value);
  });
  const returnTo = returnToUrl.toString();

  const url = `${appConfig.apiBaseUrl}${urls.session.logout}?returnTo=${encodeURIComponent(returnTo)}`;
  window.location.replace(url);
};
